import { cn } from "@/lib/utils";
import { forwardRef } from "react";

// ============================================
// 타입 정의
// ============================================

interface SidebarCategoryProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** 카테고리 아이콘 */
  icon: React.ReactNode;
  /** 아이콘 회전 각도 (deg) */
  rotate?: number;
  children: React.ReactNode;
}

// ============================================
// 스타일 정의
// ============================================

const baseStyles = cn(
  "group relative flex w-full items-center gap-1 rounded-small p-1",
  "transition-colors cursor-pointer",
  "hover:bg-slate-50",
  "active:bg-slate-100",
);

// caption-1-semibold: 12px, semibold, line-height 1.334, tracking 0.3024px
const textStyles = cn(
  "flex-1 truncate text-left",
  "text-xs font-semibold leading-[1.334] tracking-[0.3px]",
  "text-neutral-500",
);

// ============================================
// SidebarCategory 컴포넌트
// ============================================

export const SidebarCategory = forwardRef<
  HTMLButtonElement,
  SidebarCategoryProps
>(({ icon, rotate = 0, className, children, onClick, ...props }, ref) => {
  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    // 클릭 후 focus 해제하여 hover 스타일 제거
    e.currentTarget.blur();
    onClick?.(e);
  };

  return (
    <button
      ref={ref}
      type="button"
      className={cn(baseStyles, className)}
      onClick={handleClick}
      {...props}
    >
      {/* Icon */}
      <span
        className="flex items-center justify-center size-4 shrink-0 text-secondary-base transition-transform duration-200"
        style={{ transform: `rotate(${rotate}deg)` }}
      >
        {icon}
      </span>

      {/* Label */}
      <span className={textStyles}>{children}</span>
    </button>
  );
});

SidebarCategory.displayName = "SidebarCategory";

export default SidebarCategory;
